import type { CaptureResult } from 'posthog-js'

/**
 * PostHog autocaptures every uncaught `TRPCClientError` as a `$exception`
 * event. Its default fingerprint is derived from the minified stack — which is
 * identical for *every* tRPC error (they all originate in the same
 * `TRPCClientError` constructor frame in the bundle). The result is one
 * catch-all Error Tracking issue that mixes unrelated failures
 * ("Asset key not owned by user", "Email already registered", "Internal Server
 * Error", …) and is impossible to triage.
 *
 * This hook gives tRPC exceptions a custom `$exception_fingerprint` keyed on
 * the exception type + a normalized message, so each distinct failure becomes
 * its own issue. The message is normalized (ids/uuids collapsed) so dynamic
 * suffixes like "…user 7" don't explode into a new issue per id.
 *
 * Note: suppression of *expected* tRPC codes (FORBIDDEN/NOT_FOUND/…) for
 * caught errors lives in `captureError` (`lib/errors.ts`). Uncaught rejections
 * bypass that path, so the safest long-term fix is to stop leaking unhandled
 * expected rejections at the mutation boundaries — this hook only fixes the
 * grouping of whatever does reach Error Tracking.
 */

/** Collapse ids, uuids, and long hex so they don't fragment the fingerprint. */
const normalizeMessage = (value: string): string =>
  value
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '#uuid')
    .replace(/\b\d+\b/g, '#')
    .trim()
    .slice(0, 120)

type ExceptionListEntry = { type?: unknown; value?: unknown }

export const fingerprintTrpcErrors = (result: CaptureResult | null): CaptureResult | null => {
  if (!result || result.event !== '$exception') return result

  const list = result.properties?.['$exception_list']
  const first: ExceptionListEntry | undefined = Array.isArray(list) ? list[0] : undefined
  const type = typeof first?.type === 'string' ? first.type : undefined
  const value = typeof first?.value === 'string' ? first.value : undefined

  // Only retag tRPC client errors; leave everything else on its default grouping.
  if (!type || !type.includes('TRPCClientError')) return result

  // A custom fingerprint already set upstream (e.g. via captureException) wins.
  if (result.properties && !result.properties['$exception_fingerprint']) {
    result.properties['$exception_fingerprint'] = value
      ? `${type}: ${normalizeMessage(value)}`
      : type
  }

  return result
}
